
import { debounce } from "lodash";
import { computed, defineComponent, ref } from "vue";
import { useRoute } from "vue-router";
import { useRouterCustom } from "@/core/hooks/use-router-custom";
import {
  IDanhMucVatTuResponseBase,
  IVatTuResponseBase,
} from "@/core/interfaces/ApiResponses";
import { VatTuService } from "@/core/services/VatTu.service";
import { swalNotification } from "@/core/helpers/utils";
import { usePagination } from "@/core/hooks/use-pagination";
import { usePageHeader } from "@/core/hooks/usePageHeader";
import { VAT_TU } from "@/core/constants/permissions";
import { faPrint } from "@fortawesome/free-solid-svg-icons";
import DanhSachVatTuTable from "@/views/crafted/pages/vat-tu/danh-sach-vat-tu-table/DanhSachVatTuTable.vue";
import Pagination from "@/components/shared/pagination/Pagination.vue";
import PageContentBase from "@/layout/_shared/PageContentBase.vue";
import PageHeaderFilters from "@/layout/header/PageHeaderFilters.vue";
import PageHeaderActions from "@/layout/header/PageHeaderActions.vue";
import PageHeaderActionItem from "@/layout/header/PageHeaderActionItem.vue";
import FilterItem from "@/layout/_shared/FilterItem.vue";
import DanhMucVatTuSearchInput from "@/components/shared/danh-muc-vat-tu-search-input/DanhMucVatTuSearchInput.vue";

export default defineComponent({
  name: "danh-sach-vat-tu",

  components: {
    DanhMucVatTuSearchInput,
    FilterItem,
    Pagination,
    PageContentBase,
    PageHeaderFilters,
    PageHeaderActions,
    PageHeaderActionItem,
    DanhSachVatTuTable,
  },

  setup() {
    const { name: routeName } = useRoute();
    const { push } = useRouterCustom();

    const {
      currentPage,
      totalItems,
      perPage,
      perPageArr,
      loading,
    } = usePagination();

    const validStatus = ref<0 | 1 | -1>(-1);
    const dmVatTuId = ref<number | null>(null);
    const selectedDmVatTuLabel = ref('');
    const thuHoiFilter = ref(false);
    const congTyDuyetFilter = ref(false);

    const isInaccessible = computed(() => {
      if (routeName === "update-supply")
        return process.env.VUE_APP_IS_EDIT_VAT_TU_ENABLE === "false";

      return process.env.VUE_APP_IS_ADD_VAT_TU_ENABLE === "false";
    });

    document.title = "Vật tư - Đường sắt Hà Nội";

    const { searchQuery } = usePageHeader({
      title: "Danh sách vật tư",
      breadcrumbs: [{ text: "Vật tư" }],
      showSearch: true,
      searchPlaceholder: "Tìm theo mã hoặc tên",
      ...(!isInaccessible.value && {
        addLabel: "Thêm vật tư",
        addPermissions: [VAT_TU.TAO_MOI],
        onAdd: () => push("/quan-ly/vat-tu/tao-moi"),
      }),
    });

    return {
      dmVatTuId,
      selectedDmVatTuLabel,
      push,
      searchQuery,
      currentPage,
      isInaccessible,
      perPage,
      totalItems,
      perPageArr,
      loading,
      validStatus,
      thuHoiFilter,
      congTyDuyetFilter,
      faPrint,
    };
  },

  data() {
    return {
      items: [] as IVatTuResponseBase[],
      statuses: [
        {
          label: "Tất cả trạng thái",
          value: -1,
        },
        {
          label: "Đã duyệt",
          value: 1,
        },
        {
          label: "Chưa duyệt",
          value: 0,
        },
      ],
    };
  },

  async mounted() {
    await this.fetchVatTuItems();
  },

  watch: {
    async currentPage() {
      await this.fetchVatTuItems();
    },

    async perPage() {
      if (this.currentPage === 1) {
        await this.fetchVatTuItems();
        return;
      }
      this.currentPage = 1;
    },

    async validStatus() {
      await this.fetchVatTuItems();
    },

    async dmVatTuId() {
      this.currentPage = 1;
      await this.fetchVatTuItems();
    },

    async thuHoiFilter() {
      this.currentPage = 1;
      await this.fetchVatTuItems();
    },

    async congTyDuyetFilter() {
      this.currentPage = 1;
      await this.fetchVatTuItems();
    },

    searchQuery: debounce(function () {
      this.fetchVatTuItems();
    }, 500),
  },

  methods: {
    openPrintViewer() {
      const tenDanhMuc =
        this.selectedDmVatTuLabel || (this.dmVatTuId ? '' : 'Tất cả nhóm');

      const route = this.$router.resolve({
        name: 'xem-danh-sach-vat-tu',
        params: { idDmVatTu: String(this.dmVatTuId || 0) },
        query: tenDanhMuc ? { ten: tenDanhMuc } : {},
      });

      window.open(route.href, '_blank');
    },

    clearFilter() {
      this.dmVatTuId = null;
      this.selectedDmVatTuLabel = '';
      this.validStatus = -1;
      this.thuHoiFilter = false;
      this.congTyDuyetFilter = false;
      this.currentPage = 1;
      this.fetchVatTuItems();
    },

    async fetchVatTuItems() {
      try {
        this.loading = true;

        const {
          data: {
            data: { data, total },
          },
        } = await VatTuService.list({
          per_page: this.perPage,
          current_page:
            this.searchQuery ||
            (this.validStatus !== -1 &&
              this.validStatus !== undefined &&
              this.validStatus !== null) ||
            this.dmVatTuId ||
            this.thuHoiFilter ||
            this.congTyDuyetFilter
              ? 1
              : this.currentPage,
          s: this.searchQuery,
          ...(this.dmVatTuId && {
            id_dm_vat_tu: this.dmVatTuId,
          }),
          ...(this.validStatus !== -1 && {
            is_valid: this.validStatus,
          }),
          ...(this.thuHoiFilter && { thu_hoi: 1 }),
          ...(this.congTyDuyetFilter && { cong_ty_duyet: 1 }),
        });

        this.items = data;
        this.totalItems = total;
        this.loading = false;
      } catch {
        await swalNotification(
          "Có lỗi xảy ra khi lấy thông tin danh sách vật tư",
          "error",
        );
        this.loading = false;
      }
    },

    handleDmClear() {
      this.dmVatTuId = null;
      this.selectedDmVatTuLabel = '';
    },

    handleDmSelect({ id, ten_danh_muc }: IDanhMucVatTuResponseBase) {
      this.dmVatTuId = id;
      this.selectedDmVatTuLabel = ten_danh_muc;
    },
  },
});
